/**
 * DNH — Subscribe Page (Badge-based tier selection).
 *
 * Single pricing card. Badge pills select the tier — price, discount, and pitch copy
 * all update dynamically. No boxes, no confusion, no restriction feel.
 */
import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, Sparkles, ShieldCheck, Globe2 } from "lucide-react";

import {
  SiteHeader,
  SiteFooter,
  FORMATTED_DATE,
} from "../components/dnh/SiteChrome";
import { useAuth } from "../components/dnh/AuthContext";
import { Button } from "../components/ui/button";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

const TIERS = [
  {
    id: "student",
    label: "Student",
    price: 12,
    monthly: "1",
    discount: 90,
    pitch: "You're investing in knowledge, not yet in income. DNH gives you the same global intelligence briefings that cost professionals 5× more — for the price of a coffee each month.",
  },
  {
    id: "job_seeker",
    label: "Job Seeker",
    price: 24,
    monthly: "2",
    discount: 80,
    pitch: "Between roles is the best time to stay sharp. Stay ahead of the market, industries and geopolitics while you make your next move — at 80% below the standard price.",
  },
  {
    id: "professional",
    label: "Professional",
    price: 60,
    monthly: "5",
    discount: 50,
    pitch: "You read the news to make better decisions. DNH cuts through noise with AI-curated intelligence briefs across 196 countries — in the time it takes to finish your morning coffee.",
  },
  {
    id: "business",
    label: "Business Leader",
    price: 120,
    monthly: "10",
    discount: 0,
    pitch: "For leaders who need the full picture. DNH Premium is your daily briefing room — global intelligence, geopolitical signals, and topic narratives that keep your thinking three steps ahead.",
  },
];

const ANCHOR_PRICE = 120;

const FEATURES = [
  {
    icon: Sparkles,
    title: "Every brief unlocked",
    body: "Read every full intelligence brief — no daily limits, no preview cuts.",
  },
  {
    icon: Globe2,
    title: "Every country lens",
    body: "Reframe global news through any of 196 countries. Switch perspectives instantly.",
  },
  {
    icon: ShieldCheck,
    title: "Source diversity & confidence",
    body: "See how many independent sources back each story. Decide on signal, not noise.",
  },
];

export default function Subscribe() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const canceled = params.get("canceled") === "1";
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [forceLive, setForceLive] = useState(false);
  const [config, setConfig] = useState(null);
  const [selectedTier, setSelectedTier] = useState("student");
  const checkoutId = useRef(null);

  const isAdmin = !!config?.can_test;
  const activeTier = TIERS.find(t => t.id === selectedTier) || TIERS[0];

  useEffect(() => {
    if (loading) return;
    let active = true;
    setConfig(null); setErr(""); checkoutId.current = null;
    fetch(`${BASE_URL}/api/subscription/config?force_live=${forceLive}`, { credentials: "include" })
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.detail || "Payment configuration unavailable"); return d; })
      .then(d => { if (active) setConfig(d); })
      .catch(e => { if (active) setErr(e.message); });
    return () => { active = false; };
  }, [loading, user?.user_id, forceLive]);

  useEffect(() => {
    if (!loading && user?.subscription_tier === "premium" && !user?.is_admin) {
      navigate("/account", { replace: true });
    }
  }, [loading, user, navigate]);

  const startCheckout = async () => {
    setErr("");
    if (!user) {
      navigate(`/signin?from=${encodeURIComponent("/subscribe")}`);
      return;
    }
    setBusy(true);
    try {
      checkoutId.current ||= crypto.randomUUID();
      const body = { origin: window.location.origin, request_id: checkoutId.current, tier: selectedTier };
      if (isAdmin && forceLive) body.force_live = true;
      const res = await fetch(`${BASE_URL}/api/subscription/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const failure = await res.json();
        throw new Error(typeof failure.detail === "string" ? failure.detail : "Checkout failed");
      }
      const { url } = await res.json();
      if (!url) throw new Error("No checkout URL");
      window.location.href = url;
    } catch (e) {
      setErr(e.message || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--paper)] text-[var(--ink)]">
      <SiteHeader activeNav="Subscribe" ticker />

      <main className="mx-auto max-w-[1080px] px-4 sm:px-6 lg:px-10 py-14 sm:py-20">

        {/* Header */}
        <div className="text-center max-w-2xl mx-auto">
          <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[var(--crimson)]">
            DNH Premium · {FORMATTED_DATE.split(",")[0] || "Today"}
          </div>
          <h1
            className="font-serif text-[42px] sm:text-[58px] leading-[1.02] tracking-[-0.025em] text-[var(--ink)] mt-4"
            data-testid="subscribe-headline"
          >
            News Intelligence,{" "}
            <span className="italic text-[var(--crimson)]">priced for you.</span>
          </h1>
          <p className="font-sans text-[15px] sm:text-[17px] text-[var(--ink-2)] leading-[1.55] mt-5 max-w-xl mx-auto">
            All tiers unlock every feature. Choose the one that fits your situation — everyone deserves access to good intelligence.
          </p>
        </div>

        {canceled && (
          <div
            className="mt-8 mx-auto max-w-md rounded-[10px] border border-[var(--hairline)] bg-[var(--surface-2)] p-4 text-center font-mono text-[11px] tracking-[0.06em] text-[var(--ink-2)]"
            data-testid="subscribe-canceled-notice"
          >
            Checkout canceled — you can subscribe whenever you're ready.
          </div>
        )}

        {/* Badge pill selector */}
        <div
          className="mt-10 flex flex-wrap justify-center gap-2.5"
          data-testid="subscribe-tier-badges"
        >
          {TIERS.map(tier => {
            const isSelected = selectedTier === tier.id;
            return (
              <button
                key={tier.id}
                type="button"
                onClick={() => { setSelectedTier(tier.id); setErr(""); checkoutId.current = null; }}
                data-testid={`tier-badge-${tier.id}`}
                className={[
                  "relative h-10 px-5 rounded-full font-mono text-[11.5px] tracking-[0.12em] uppercase transition-all",
                  isSelected
                    ? "bg-[var(--crimson)] text-[var(--paper)] shadow-[0_2px_12px_rgba(192,57,43,0.35)]"
                    : "bg-[var(--paper)] text-[var(--ink-2)] border border-[var(--hairline)] hover:border-[var(--crimson)]/50 hover:text-[var(--ink)]",
                ].join(" ")}
              >
                {tier.label}
                {tier.discount > 0 && (
                  <span className={[
                    "ml-2 text-[9px] font-semibold tracking-[0.08em]",
                    isSelected ? "text-[var(--paper)]/80" : "text-[var(--crimson)]",
                  ].join(" ")}>
                    {tier.discount}% OFF
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <p className="mt-3 text-center font-mono text-[10px] tracking-[0.08em] text-[var(--muted)]">
          Self-declared · honour system · all tiers unlock every feature
        </p>

        {/* Single dynamic pricing card */}
        <div className="mt-8 max-w-[560px] mx-auto">
          <div
            className="relative rounded-[20px] border border-[var(--crimson)]/40 bg-[var(--paper)] p-7 sm:p-9 shadow-[var(--shadow-soft)]"
            data-testid="subscribe-plan-card"
          >
            <div
              className="absolute -top-3 left-7 inline-flex items-center font-mono text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--paper)] bg-[var(--crimson)] rounded-full px-3 py-1"
              data-testid="subscribe-plan-badge"
            >
              {activeTier.label} · billed yearly
            </div>

            {/* Price row */}
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--muted)]">
                  DNH Premium
                </div>
                <div className="flex items-baseline gap-2 mt-1.5">
                  {activeTier.price < ANCHOR_PRICE && (
                    <span className="font-mono text-[20px] text-[var(--muted)] line-through leading-none">
                      ${ANCHOR_PRICE}
                    </span>
                  )}
                  <span className="font-serif text-[56px] leading-none text-[var(--ink)]">
                    ${activeTier.price}
                  </span>
                  <span className="font-mono text-[12px] tracking-[0.14em] uppercase text-[var(--muted)]">
                    / year
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="font-mono text-[11px] text-[var(--muted)]">
                    ~${activeTier.monthly} / month
                  </div>
                  {activeTier.discount > 0 && (
                    <div className="font-mono text-[9px] tracking-[0.1em] uppercase bg-[var(--crimson)]/10 text-[var(--crimson)] rounded-full px-2 py-0.5">
                      {activeTier.discount}% off
                    </div>
                  )}
                </div>
              </div>
              <Sparkles size={36} strokeWidth={1.4} className="text-[var(--crimson)] opacity-80 shrink-0" />
            </div>

            {/* Dynamic AI pitch */}
            <p
              className="mt-5 font-sans text-[13.5px] text-[var(--ink-2)] leading-[1.6] border-l-2 border-[var(--crimson)]/30 pl-3.5 italic"
              data-testid="subscribe-pitch-copy"
            >
              {activeTier.pitch}
            </p>

            {/* Feature list */}
            <ul className="mt-6 space-y-2.5">
              {[
                "Subscriber-only intelligence briefs",
                "Unlimited reading from the premium brief library",
                "All editorial topics & trending narratives",
                "Country Lenses — read the world through any country",
                "Brief archive + saved reading list",
                "Cancel anytime from your Stripe billing portal",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2.5 text-[13.5px] text-[var(--ink-2)] leading-[1.45]">
                  <Check size={14} strokeWidth={2.25} className="text-[var(--crimson)] mt-[3px] shrink-0" />
                  {b}
                </li>
              ))}
            </ul>

            <Button
              type="button"
              onClick={startCheckout}
              disabled={busy || (user && (!config || !config.payments_enabled))}
              data-testid="subscribe-checkout-button"
              className="w-full mt-7 h-14 rounded-[10px] bg-[var(--ink)] text-[var(--paper)] hover:bg-[var(--crimson)] font-medium text-[14.5px] transition-colors disabled:opacity-60"
            >
              {busy
                ? "Redirecting to Stripe…"
                : user
                ? `Continue as ${activeTier.label} — $${activeTier.price}/yr →`
                : "Sign in to subscribe →"}
            </Button>

            {/* Admin-only: live mode toggle */}
            {isAdmin && (
              <div className="mt-4 flex items-center justify-center gap-2" data-testid="admin-stripe-toggle">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input data-testid="stripe-live-mode-toggle" type="checkbox" checked={forceLive} onChange={e => setForceLive(e.target.checked)} />
                  <span data-testid="stripe-checkout-mode" className="font-mono text-[10px] text-[var(--muted)] uppercase tracking-[0.1em]">
                    {forceLive ? "LIVE mode (real charge)" : "TEST mode (sandbox — default)"}
                  </span>
                </label>
              </div>
            )}

            {config && !config.payments_enabled && (
              <p data-testid="stripe-unavailable" role="alert" className="mt-4 text-sm text-[var(--crimson)] text-center">
                Subscriptions are not open in {config.mode} mode yet. No charge will be started.
              </p>
            )}

            {err && (
              <div className="mt-3 font-mono text-[11px] text-[var(--crimson)] text-center" data-testid="subscribe-error">
                {err}
              </div>
            )}

            <div className="mt-4 font-mono text-[10px] text-[var(--muted)] text-center tracking-[0.06em]">
              Secured by Stripe · No charges until you confirm · Have a promo code? Apply it at checkout.
            </div>
          </div>
        </div>

        {/* Feature triplet */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-[14px] border border-[var(--hairline)] bg-[var(--paper)] p-5">
              <f.icon size={20} strokeWidth={1.6} className="text-[var(--crimson)]" />
              <h3 className="font-serif text-[18px] leading-tight text-[var(--ink)] mt-3">{f.title}</h3>
              <p className="font-sans text-[13px] text-[var(--ink-2)] leading-[1.55] mt-2">{f.body}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <Link to="/" className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-[var(--muted)] hover:text-[var(--crimson)] dnh-link-underline">
            ← Back to home
          </Link>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
